/**
 * Asset - Sistema unificado de assets
 *
 * Fusiona ThemeManager + AssetPreloader + AssetManager en una sola API simple.
 *
 * @example
 * // Inicialización (startup)
 * await Asset.init('/alberath/theme/base/theme.json');
 *
 * // Uso simple
 * Asset.img('unit.pyromancer')        // → URL string
 * Asset.img('unit.pyromancer:small')  // → URL con variante
 * Asset.sound('button')               // → Howl instance
 * Asset.music('maintheme')            // → Howl instance
 * Asset.icon('fire')                  // → React Component
 * Asset.icon('fire:small')            // → React Component con size preset
 *
 * // Precarga
 * Asset.preload(['unit.pyromancer', 'bg.login', 'sound.button'])
 */

import { Howl } from 'howler';
import { Storage } from '@storage';
import useConfigStore from '@/store/useConfigStore';

// ============================================================
// STATE
// ============================================================

const state = {
  // Theme config - primary theme (first in stack)
  theme: null,

  // Theme stack (v2.0) - array of loaded themes for cascade resolution
  // First theme = highest priority, last = fallback (usually 'base')
  stack: [],
  loadedThemes: new Map(), // slug -> theme config

  mappings: new Map(), // sourceUrl -> mapping data (icons, videos, etc.)
  styleElements: [], // Injected <link> elements for theme CSS

  // Preload queue
  queue: [],
  currentBatch: null,
  isPreloading: false,
  progress: 0,
  label: '',
  listeners: new Set(),

  // Initialization
  initialized: false,
  initPromise: null,
};

// Cache en memoria de assets (para acceso síncrono)
// Key format: 'category.key' → { type, blobUrl?, howl?, fromCache }
const memoryCache = new Map();

// Icon size presets
const ICON_SIZES = {
  xs: 12,
  small: 16,
  medium: 24,
  large: 32,
  xl: 48,
};

// ============================================================
// INITIALIZATION
// ============================================================

/**
 * Inicializa el sistema de assets
 * @param {string} themeUrl - URL del theme.json
 */
async function init(themeUrl) {
  if (state.initialized && state.theme) return;
  if (state.initPromise) return state.initPromise;

  state.initPromise = _doInit(themeUrl);
  await state.initPromise;
}

async function _doInit(themeUrl) {
  try {
    // Intentar restaurar theme de Storage
    const cached = await Storage.get('theme', 'current');
    if (cached && !themeUrl) {
      state.theme = cached;
      await loadStyles();
      console.log(`[🎨 Asset] Restored theme: ${cached.slug} (v${cached.version})`);
      state.initialized = true;
      return;
    }

    // Cargar theme desde URL
    if (themeUrl) {
      await loadTheme(themeUrl);
    }

    state.initialized = true;
  } catch (error) {
    console.error('[Asset] Failed to initialize:', error);
    state.initialized = true; // Continue anyway
  }
}

/**
 * Carga un theme desde URL
 * @param {string} url - URL del theme.json
 * @param {boolean} force - Forzar recarga aunque ya haya un tema
 */
async function loadTheme(url, force = false) {
  if (state.theme && !force) {
    if (state.styleElements.length === 0 && state.theme.styles?.length > 0) {
      await loadStyles();
    }
    return;
  }

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const theme = await response.json();

    if (!theme || !theme.slug || !theme.version || !theme.sources) {
      throw new Error('Invalid theme config');
    }

    state.theme = theme;
    state.loadedThemes.set(theme.slug, theme);
    state.stack = [theme.slug];
    state.mappings.clear();

    // Clear memory cache to force reload of assets from new theme
    clearMemory();

    await loadStyles();

    // Auto-preload if theme has preload config
    if (theme.preload) {
      const { music = [], sounds = [], images = [] } = theme.preload;
      const totalAssets = music.length + sounds.length + images.length;
      if (totalAssets > 0) {
        console.log(`[🎨 Asset] Preloading ${totalAssets} theme assets...`);
        preload({ label: `Loading ${theme.name}`, music, sounds, images });
        await waitForComplete();
        console.log(`[🎨 Asset] Preloaded ${totalAssets} assets`);
      }
    }

    // If this is the base theme, update config
    if (theme.isBase) {
      useConfigStore.getState().updateSetting('baseTheme', theme.slug);
    }

    // If this is NOT the base theme, ensure base theme is loaded as fallback
    if (!theme.isBase && !_findBaseTheme()) {
      const baseUrl = url.replace(/\/[^/]+\/theme\.json$/, '/base/theme.json');
      try {
        const baseResponse = await fetch(baseUrl);
        if (baseResponse.ok) {
          const baseTheme = await baseResponse.json();
          if (baseTheme && baseTheme.isBase) {
            state.loadedThemes.set(baseTheme.slug, baseTheme);
            useConfigStore.getState().updateSetting('baseTheme', baseTheme.slug);
            console.log(`[🎨 Asset] Loaded base theme "${baseTheme.slug}" as fallback`);
          }
        }
      } catch (error) {
        console.warn('[Asset] Could not load base theme for fallback:', error.message);
      }
    }

    // Persist (async, no blocking)
    Storage.set('theme', 'current', theme).catch((error) => {
      console.warn('[Asset] Failed to persist theme:', error);
    });
    Storage.set('theme', 'stack', state.stack).catch(() => {});

    console.log(`[🎨 Asset] Loaded theme: ${theme.slug} (v${theme.version})`);

    // Validate single base theme
    _validateSingleBaseTheme();

    // Notify listeners of theme change
    _notify();
  } catch (error) {
    console.error('[Asset] Failed to load theme:', error);
    throw error;
  }
}

/**
 * Carga un stack de temas (Theme Stack v2.0)
 * @param {string[]} slugs - Array de slugs en orden de prioridad (primero = máxima prioridad)
 *
 * @example
 * await Asset.loadStack(['halloween', 'dark', 'base']);
 * // halloween overrides → dark overrides → base (fallback)
 */
async function loadStack(slugs) {
  if (!slugs || !Array.isArray(slugs) || slugs.length === 0) {
    throw new Error('[Asset] loadStack requires non-empty array of theme slugs');
  }

  const brand = import.meta.env.VITE_GAME_BRAND;
  const loadedSlugs = [];

  // Load each theme
  for (const slug of slugs) {
    if (state.loadedThemes.has(slug)) {
      loadedSlugs.push(slug);
      continue;
    }

    try {
      const url = `/${brand}/theme/${slug}/theme.json`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const theme = await response.json();

      if (!theme || !theme.slug || !theme.version || !theme.sources) {
        throw new Error(`Invalid theme config for "${slug}"`);
      }

      state.loadedThemes.set(slug, theme);
      loadedSlugs.push(slug);
    } catch (error) {
      console.error(`[Asset] Failed to load theme "${slug}":`, error);
      // Continue with other themes
    }
  }

  if (loadedSlugs.length === 0) {
    throw new Error('[Asset] No themes could be loaded');
  }

  // Update stack and primary theme
  state.stack = loadedSlugs;
  state.theme = state.loadedThemes.get(loadedSlugs[0]);
  state.mappings.clear();

  // Load CSS from all themes (in reverse order for proper cascade)
  await loadStackStyles();

  // Persist
  Storage.set('theme', 'current', state.theme).catch(() => {});
  Storage.set('theme', 'stack', state.stack).catch(() => {});

  // Validate single base theme
  _validateSingleBaseTheme();

  console.log(`[🎨 Asset] Stack loaded: [${loadedSlugs.join(' → ')}]`);
}

/**
 * Carga el theme config directamente (sin fetch)
 * @param {Object} theme - Theme config object
 */
async function setTheme(theme) {
  if (!theme || !theme.slug || !theme.version || !theme.sources) {
    throw new Error('[Asset] Invalid theme config');
  }

  state.theme = theme;
  state.mappings.clear();

  await loadStyles();

  // Persist (async, no blocking)
  Storage.set('theme', 'current', theme).catch((error) => {
    console.warn('[Asset] Failed to persist theme:', error);
  });

  console.log(`[🎨 Asset] Theme set: ${theme.slug} (v${theme.version})`);
}

// ============================================================
// THEME STYLES (CSS)
// ============================================================

/**
 * Carga los archivos CSS del tema
 */
async function loadStyles() {
  if (!state.theme) {
    throw new Error('[Asset] No theme loaded');
  }

  const styles = state.theme.styles;
  if (!styles || !Array.isArray(styles) || styles.length === 0) {
    return;
  }

  // Remove old styles
  unloadStyles();

  const brand = import.meta.env.VITE_GAME_BRAND;
  const baseUrl = `/${brand}/theme/${state.theme.slug}/`;
  const version = state.theme.version;

  const loadPromises = styles.map((filename, index) => {
    return new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.type = 'text/css';
      link.href = `${baseUrl}${filename}?v=${version}`;
      link.dataset.themeStyle = 'true';
      link.dataset.order = index.toString();

      link.onload = () => resolve();
      link.onerror = () => {
        console.error(`[Asset] Failed to load CSS: ${filename}`);
        reject(new Error(`Failed to load ${filename}`));
      };

      state.styleElements.push(link);
      document.head.appendChild(link);
    });
  });

  await Promise.all(loadPromises);
}

/**
 * Remueve todos los estilos CSS del tema
 */
function unloadStyles() {
  for (const link of state.styleElements) {
    if (link.parentNode) {
      link.parentNode.removeChild(link);
    }
  }
  state.styleElements = [];
}

/**
 * Carga CSS de todos los temas en el stack (Theme Stack v2.0)
 * Los temas se cargan en orden inverso para que el primero tenga mayor prioridad
 */
async function loadStackStyles() {
  if (state.stack.length === 0) return;

  // Remove old styles
  unloadStyles();

  const brand = import.meta.env.VITE_GAME_BRAND;

  // Load in reverse order: base first, then overrides
  // This way, the first theme's CSS has highest specificity
  const reversedStack = [...state.stack].reverse();

  let order = 0;
  for (const slug of reversedStack) {
    const theme = state.loadedThemes.get(slug);
    if (!theme || !theme.styles || !Array.isArray(theme.styles)) continue;

    const baseUrl = `/${brand}/theme/${slug}/`;
    const version = theme.version;

    for (const filename of theme.styles) {
      await new Promise((resolve, reject) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.type = 'text/css';
        link.href = `${baseUrl}${filename}?v=${version}`;
        link.dataset.themeStyle = 'true';
        link.dataset.themeSlug = slug;
        link.dataset.order = (order++).toString();

        link.onload = () => resolve();
        link.onerror = () => {
          console.error(`[Asset] Failed to load CSS: ${slug}/${filename}`);
          resolve(); // Continue anyway
        };

        state.styleElements.push(link);
        document.head.appendChild(link);
      });
    }
  }
}

// ============================================================
// BASE THEME HELPERS
// ============================================================

/**
 * Busca el tema base (con isBase: true) en loadedThemes
 * Primero verifica si ya lo conocemos desde config, sino busca por isBase
 * @returns {{ slug: string, theme: object }|null}
 */
function _findBaseTheme() {
  // 1. Primero buscar en config (más rápido si ya lo guardamos)
  const configBaseTheme = useConfigStore.getState().settings.baseTheme;
  if (configBaseTheme && state.loadedThemes.has(configBaseTheme)) {
    return { slug: configBaseTheme, theme: state.loadedThemes.get(configBaseTheme) };
  }

  // 2. Si no está en config, buscar en loadedThemes por isBase: true
  for (const [slug, theme] of state.loadedThemes.entries()) {
    if (theme.isBase === true) {
      // Guardar en config para futuras búsquedas
      useConfigStore.getState().updateSetting('baseTheme', slug);
      return { slug, theme };
    }
  }

  return null;
}

/**
 * Valida que no haya múltiples temas base cargados
 */
function _validateSingleBaseTheme() {
  const baseThemes = [...state.loadedThemes.values()].filter(t => t.isBase === true);
  if (baseThemes.length > 1) {
    console.warn('[Asset] Multiple base themes detected:', baseThemes.map(t => t.slug));
  }
}

// ============================================================
// URL BUILDING
// ============================================================

/**
 * Obtiene la config del tema
 */
function getTheme() {
  if (!state.theme) {
    throw new Error('[Asset] No theme loaded');
  }
  return state.theme;
}

/**
 * Obtiene la versión del tema
 */
function getVersion() {
  return getTheme().version;
}

/**
 * Verifica si hay un tema cargado
 */
function isLoaded() {
  return state.theme !== null;
}

/**
 * Obtiene el stack actual de temas (Theme Stack v2.0)
 * @returns {string[]} Array de slugs en orden de prioridad
 */
function getStack() {
  return [...state.stack];
}

/**
 * Obtiene un tema por slug del cache
 * @param {string} slug - Slug del tema
 * @returns {object|null} Theme config o null si no está cargado
 */
function getLoadedTheme(slug) {
  return state.loadedThemes.get(slug) || null;
}

/**
 * Obtiene la config de source para una categoría
 * Busca en: tema actual → temas del stack → tema base (fallback)
 */
function getSource(category) {
  // 1. Primero intentar en el tema actual
  const primaryTheme = getTheme();
  if (primaryTheme.sources[category]) {
    return primaryTheme.sources[category];
  }

  // 2. Si estamos en modo stack, buscar en los demás temas
  if (state.stack.length > 1) {
    for (const slug of state.stack) {
      const theme = state.loadedThemes.get(slug);
      if (theme?.sources[category]) {
        return theme.sources[category];
      }
    }
  }

  // 3. Fallback: buscar el tema con isBase: true
  const baseThemeInfo = _findBaseTheme();
  if (baseThemeInfo && baseThemeInfo.theme.sources[category]) {
    return baseThemeInfo.theme.sources[category];
  }

  // 4. Si nada funciona, error
  throw new Error(`[Asset] No source config for category "${category}" in any loaded theme`);
}

/**
 * Construye URL para un asset
 * @param {string} category - 'images', 'sounds', 'music', 'icons', 'videos'
 * @param {string} key - Asset key (e.g., 'unit.pyromancer' or 'unit.pyromancer:small')
 * @returns {Object} { type, value, ... }
 */
function buildUrl(category, key) {
  const source = getSource(category);

  // Parse key:variant
  const [baseKey, variant] = key.split(':');

  switch (source.source) {
    case 'convention':
      return _buildConventionUrl(source, baseKey, variant);

    case 'map':
      return _buildFromMap(source, baseKey, variant);

    case 'file':
      return _buildFromFile(source, baseKey, variant);

    default:
      throw new Error(`[Asset] Unknown source type "${source.source}"`);
  }
}

function _buildConventionUrl(source, key, variant) {
  let path = key.replace(/\./g, '/');

  // Si hay variante, agregar como subdirectorio o sufijo
  if (variant) {
    path = `${path}/${variant}`;
  }

  const url = `${source.sourceUrl}${path}${source.ext}?v=${getVersion()}`;

  return {
    type: source.type,
    value: url,
  };
}

function _buildFromMap(source, key, variant) {
  const mapping = state.mappings.get(source.sourceUrl);
  if (!mapping) {
    throw new Error(`[Asset] Mapping not loaded for "${source.sourceUrl}". Call loadMapping() first.`);
  }

  const value = mapping[key];
  if (value === undefined) {
    throw new Error(`[Asset] Key "${key}" not found in mapping`);
  }

  return {
    type: source.type,
    value: value,
    variant: variant,
    library: source.library,
  };
}

function _buildFromFile(source, key, variant) {
  const id = key.replace(/\./g, '-');

  return {
    type: source.type,
    value: `${source.sourceUrl}?v=${getVersion()}`,
    id: id,
    variant: variant,
  };
}

/**
 * Carga un mapping JSON
 */
async function loadMapping(sourceUrl) {
  if (state.mappings.has(sourceUrl)) {
    return state.mappings.get(sourceUrl);
  }

  try {
    const urlWithVersion = `${sourceUrl}?v=${getVersion()}`;
    const response = await fetch(urlWithVersion);

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const mapping = await response.json();
    state.mappings.set(sourceUrl, mapping);

    return mapping;
  } catch (error) {
    throw new Error(`[Asset] Failed to load mapping "${sourceUrl}": ${error.message}`);
  }
}

// ============================================================
// PUBLIC API - ASSET GETTERS
// ============================================================

/**
 * Obtiene URL de una imagen
 * @param {string} key - 'unit.pyromancer' o 'unit.pyromancer:small'
 * @returns {string} URL (blobUrl si precargado, sino URL normal)
 */
function img(key) {
  const cacheKey = `images.${key}`;

  // Si está en memoria, retornar blobUrl
  if (memoryCache.has(cacheKey)) {
    const cached = memoryCache.get(cacheKey);
    return cached.blobUrl || cached.url;
  }

  // Si no, construir URL normal
  const urlInfo = buildUrl('images', key);
  return urlInfo.value;
}

/**
 * Obtiene un sonido (Howl instance)
 * @param {string} key - 'button' o 'click:loud'
 * @returns {Howl|null} Howl instance o null si no precargado
 */
function sound(key) {
  const cacheKey = `sounds.${key}`;

  if (memoryCache.has(cacheKey)) {
    return memoryCache.get(cacheKey).howl;
  }

  // Intentar desde Storage audioCache
  const cached = Storage.getAudioCache('sounds', key);
  if (cached) return cached;

  console.warn(`[Asset] Sound "${key}" not preloaded`);
  return null;
}

/**
 * Obtiene música (Howl instance)
 * @param {string} key - 'maintheme'
 * @returns {Howl|null} Howl instance o null si no precargado
 */
function music(key) {
  const cacheKey = `music.${key}`;

  if (memoryCache.has(cacheKey)) {
    return memoryCache.get(cacheKey).howl;
  }

  // Intentar desde Storage audioCache
  const cached = Storage.getAudioCache('music', key);
  if (cached) return cached;

  console.warn(`[Asset] Music "${key}" not preloaded`);
  return null;
}

/**
 * Obtiene un icono (React Component)
 * @param {string} key - 'fire' o 'fire:small'
 * @returns {React.Component} Lucide component
 */
function icon(key) {
  const [name, variant] = key.split(':');

  // Obtener el componente base del mapping
  const cacheKey = `icons.${name}`;
  let iconInfo;

  if (memoryCache.has(cacheKey)) {
    iconInfo = memoryCache.get(cacheKey);
  } else {
    // Intentar construir desde mapping
    try {
      iconInfo = buildUrl('icons', name);
      memoryCache.set(cacheKey, iconInfo);
    } catch {
      console.warn(`[Asset] Icon "${name}" not found`);
      return null;
    }
  }

  // Importar Lucide dinámicamente
  // Nota: Esto requiere que los iconos estén pre-importados o uses dynamic import
  const LucideIcons = _getLucideIcons();
  const IconComponent = LucideIcons[iconInfo.value];

  if (!IconComponent) {
    console.warn(`[Asset] Lucide icon "${iconInfo.value}" not found`);
    return null;
  }

  // Si hay variante de tamaño, wrappear con size preset
  if (variant && ICON_SIZES[variant]) {
    const size = ICON_SIZES[variant];
    return (props) => IconComponent({ size, ...props });
  }

  return IconComponent;
}

/**
 * Obtiene info de un video
 * @param {string} key - 'tutorial.intro' o 'tutorial.intro:hd'
 * @returns {Object} { type, url, id? }
 */
function video(key) {
  try {
    return buildUrl('videos', key);
  } catch {
    console.warn(`[Asset] Video "${key}" not found`);
    return null;
  }
}

/**
 * Obtiene un asset genérico
 * @param {string} key - 'images.unit.pyromancer' o 'unit.pyromancer'
 * @returns {Object} Asset data
 */
function get(category, key) {
  const cacheKey = `${category}.${key}`;

  if (memoryCache.has(cacheKey)) {
    return memoryCache.get(cacheKey);
  }

  throw new Error(`[Asset] Asset "${cacheKey}" not found in cache`);
}

/**
 * Verifica si un asset está en cache
 */
function has(category, key) {
  return memoryCache.has(`${category}.${key}`);
}

// Lucide icons cache (lazy loaded)
let _lucideIcons = null;

function _getLucideIcons() {
  if (!_lucideIcons) {
    // Dynamic import at runtime - this should be pre-bundled
    _lucideIcons = require('lucide-react');
  }
  return _lucideIcons;
}

// ============================================================
// PRELOADING
// ============================================================

/**
 * Suscribe a cambios de estado de precarga
 */
function subscribe(listener) {
  state.listeners.add(listener);
  return () => state.listeners.delete(listener);
}

/**
 * Obtiene el estado actual (precarga + tema)
 */
function getState() {
  return {
    isPreloading: state.isPreloading,
    progress: state.progress,
    label: state.label,
    themeSlug: state.theme?.slug || null,
    themeVersion: state.theme?.version || null,
  };
}

/**
 * Notifica a listeners
 */
function _notify() {
  state.listeners.forEach((listener) => listener(getState()));
}

/**
 * Verifica si está precargando
 */
function isPreloading() {
  return state.isPreloading;
}

/**
 * Encola assets para precarga
 * @param {Object|Array} assets - Batch de assets o array de keys
 *
 * Formatos aceptados:
 * - Array: ['unit.pyromancer', 'bg.login', 'sound.button']
 * - Object: { label, images: [], sounds: [], music: [] }
 */
function preload(assets) {
  if (!assets) return;

  let batch;

  if (Array.isArray(assets)) {
    // Convertir array a batch object
    batch = _arrayToBatch(assets);
  } else {
    batch = {
      label: assets.label || 'Cargando recursos',
      images: assets.images || [],
      sounds: assets.sounds || [],
      music: assets.music || [],
      icons: assets.icons || [],
    };
  }

  // Guardar lista de música agrupada por categoría ANTES de filtrar
  // (para que la lista esté disponible aunque los tracks ya estén en memoria)
  if (batch.music && batch.music.length > 0) {
    const categorized = {};
    for (const track of batch.music) {
      const parts = track.split('/');
      if (parts.length > 1) {
        const category = parts[0]; // 'battle' de 'battle/grassy_world'
        if (!categorized[category]) categorized[category] = [];
        categorized[category].push(track);
      }
    }

    for (const [category, tracks] of Object.entries(categorized)) {
      Storage.set('assets', `music:${category}`, tracks);
      console.log(`[🎵 Asset] Saved music:${category}:`, tracks);
    }
  }

  // Filtrar assets ya en memoria
  batch.images = batch.images.filter((key) => !memoryCache.has(`images.${key}`));
  batch.sounds = batch.sounds.filter((key) => !memoryCache.has(`sounds.${key}`));
  batch.music = batch.music.filter((key) => !memoryCache.has(`music.${key}`));
  batch.icons = batch.icons.filter((key) => !memoryCache.has(`icons.${key}`));

  const totalItems = batch.images.length + batch.sounds.length + batch.music.length + batch.icons.length;

  if (totalItems === 0) return;

  state.queue.push(batch);

  if (!state.isPreloading) {
    _processQueue();
  }
}

/**
 * Alias para preload (compatibilidad)
 */
function queue(assets) {
  preload(assets);
}

/**
 * Convierte array de keys a batch object
 */
function _arrayToBatch(keys) {
  const batch = {
    label: 'Cargando recursos',
    images: [],
    sounds: [],
    music: [],
    icons: [],
  };

  for (const key of keys) {
    // Detectar categoría del key
    if (key.startsWith('sound.') || key.startsWith('sounds:')) {
      batch.sounds.push(key.replace('sounds:', '').replace('sound.', ''));
    } else if (key.startsWith('music.') || key.startsWith('music:')) {
      batch.music.push(key.replace('music:', '').replace('music.', ''));
    } else if (key.startsWith('icon.') || key.startsWith('icons:')) {
      batch.icons.push(key.replace('icons:', '').replace('icon.', ''));
    } else {
      // Default: images
      batch.images.push(key.replace('images:', ''));
    }
  }

  return batch;
}

/**
 * Procesa la cola de precargas
 */
async function _processQueue() {
  if (state.queue.length === 0) {
    state.isPreloading = false;
    state.progress = 0;
    state.label = '';
    _notify();
    return;
  }

  state.isPreloading = true;
  state.currentBatch = state.queue.shift();
  state.label = state.currentBatch.label;
  state.progress = 0;
  _notify();

  const batch = state.currentBatch;
  const totalItems = batch.images.length + batch.sounds.length + batch.music.length + batch.icons.length;
  let loadedItems = 0;

  const updateProgress = () => {
    loadedItems++;
    state.progress = Math.round((loadedItems / totalItems) * 100);
    _notify();
  };

  try {
    // Preload icon mappings first if needed
    if (batch.icons.length > 0) {
      try {
        const source = getSource('icons');
        if (source.source === 'map') {
          await loadMapping(source.sourceUrl);
        }
      } catch (error) {
        console.warn('[Asset] Failed to load icon mapping:', error);
      }
    }

    const promises = [];

    // Images
    for (const key of batch.images) {
      promises.push(
        _preloadImage(key)
          .then(updateProgress)
          .catch((error) => {
            console.error(`[Asset] Failed to preload image: ${key}`, error);
            updateProgress();
          })
      );
    }

    // Sounds
    for (const key of batch.sounds) {
      promises.push(
        _preloadSound(key)
          .then(updateProgress)
          .catch((error) => {
            console.error(`[Asset] Failed to preload sound: ${key}`, error);
            updateProgress();
          })
      );
    }

    // Music
    for (const key of batch.music) {
      promises.push(
        _preloadMusic(key)
          .then(updateProgress)
          .catch((error) => {
            console.error(`[Asset] Failed to preload music: ${key}`, error);
            updateProgress();
          })
      );
    }

    // Icons
    for (const key of batch.icons) {
      promises.push(
        _preloadIcon(key)
          .then(updateProgress)
          .catch((error) => {
            console.error(`[Asset] Failed to preload icon: ${key}`, error);
            updateProgress();
          })
      );
    }

    await Promise.all(promises);
  } catch (error) {
    console.error('[Asset] Batch failed:', error);
  }

  state.currentBatch = null;
  _processQueue();
}

/**
 * Espera a que termine la precarga
 */
function waitForComplete() {
  return new Promise((resolve) => {
    if (!state.isPreloading) {
      resolve();
      return;
    }

    const unsubscribe = subscribe((currentState) => {
      if (!currentState.isPreloading) {
        unsubscribe();
        resolve();
      }
    });
  });
}

// ============================================================
// PRELOAD IMPLEMENTATIONS
// ============================================================

async function _preloadImage(key) {
  const cacheKey = `images.${key}`;
  const currentVersion = getVersion();

  // Check Storage first
  const validation = await Storage.validateVersion('images', key, currentVersion);
  if (validation.valid) {
    const blobUrl = await Storage.getBlobUrl('images', key);
    if (blobUrl) {
      const asset = { type: 'webp', blobUrl, fromCache: true };
      memoryCache.set(cacheKey, asset);
      return asset;
    }
  }

  // Download
  const urlInfo = buildUrl('images', key);

  const response = await fetch(urlInfo.value);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);

  const blob = await response.blob();

  // Store in Storage
  await Storage.set('images', key, blob, { version: currentVersion });

  // Create blobUrl
  const blobUrl = URL.createObjectURL(blob);
  const asset = { type: 'webp', blobUrl, fromCache: false };
  memoryCache.set(cacheKey, asset);

  return asset;
}

async function _preloadSound(key) {
  const cacheKey = `sounds.${key}`;
  const currentVersion = getVersion();

  // Check Storage first
  const validation = await Storage.validateVersion('sounds', key, currentVersion);
  if (validation.valid) {
    const blobUrl = await Storage.getBlobUrl('sounds', key);
    if (blobUrl) {
      const howl = await _createHowl(blobUrl);
      const asset = { type: 'mp3', howl, fromCache: true };
      memoryCache.set(cacheKey, asset);
      Storage.setAudioCache('sounds', key, howl);
      return asset;
    }
  }

  // Download
  const urlInfo = buildUrl('sounds', key);

  const response = await fetch(urlInfo.value);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);

  const blob = await response.blob();

  // Store in Storage
  await Storage.set('sounds', key, blob, { version: currentVersion });

  // Create Howl
  const blobUrl = URL.createObjectURL(blob);
  const howl = await _createHowl(blobUrl);

  const asset = { type: 'mp3', howl, fromCache: false };
  memoryCache.set(cacheKey, asset);
  Storage.setAudioCache('sounds', key, howl);

  return asset;
}

async function _preloadMusic(key) {
  const cacheKey = `music.${key}`;
  const currentVersion = getVersion();

  // Check Storage first (uses OPFS for music)
  const validation = await Storage.validateVersion('music', key, currentVersion);
  if (validation.valid) {
    const blobUrl = await Storage.getBlobUrl('music', key);
    if (blobUrl) {
      const howl = await _createHowl(blobUrl, { html5: true });
      const asset = { type: 'mp3', howl, fromCache: true };
      memoryCache.set(cacheKey, asset);
      Storage.setAudioCache('music', key, howl);
      return asset;
    }
  }

  // Download
  const urlInfo = buildUrl('music', key);

  const response = await fetch(urlInfo.value);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);

  const blob = await response.blob();

  // Store in Storage (OPFS for large files)
  await Storage.set('music', key, blob, { version: currentVersion });

  // Create Howl
  const blobUrl = URL.createObjectURL(blob);
  const howl = await _createHowl(blobUrl, { html5: true });

  const asset = { type: 'mp3', howl, fromCache: false };
  memoryCache.set(cacheKey, asset);
  Storage.setAudioCache('music', key, howl);

  return asset;
}

function _createHowl(src, options = {}) {
  return new Promise((resolve, reject) => {
    const howl = new Howl({
      src: [src],
      format: ['mp3'],
      preload: true,
      html5: options.html5 || false,
      onload: () => resolve(howl),
      onloaderror: (id, error) => reject(new Error(`Howl load error: ${error}`)),
    });
  });
}

async function _preloadIcon(key) {
  const cacheKey = `icons.${key}`;

  // Icons resolve from mapping, no blob storage needed
  const asset = buildUrl('icons', key);
  memoryCache.set(cacheKey, asset);

  return asset;
}

// ============================================================
// CLEANUP
// ============================================================

/**
 * Limpia cache en memoria
 */
function clearMemory() {
  // Revoke blobUrls
  for (const [, asset] of memoryCache) {
    if (asset.blobUrl) {
      URL.revokeObjectURL(asset.blobUrl);
    }
    if (asset.howl) {
      asset.howl.unload();
    }
  }

  memoryCache.clear();
  state.queue = [];
  state.currentBatch = null;
  state.isPreloading = false;
  state.progress = 0;
  state.label = '';
  _notify();
}

/**
 * Limpia todo (memoria + storage + theme)
 */
async function clear() {
  unloadStyles();
  clearMemory();

  state.theme = null;
  state.mappings.clear();
  state.initPromise = null;

  await Promise.all([
    Storage.clearCategory('images'),
    Storage.clearCategory('sounds'),
    Storage.clearCategory('music'),
    Storage.clearCategory('theme'),
  ]);
}

// ============================================================
// HOT RELOAD (Development)
// ============================================================

if (import.meta.hot) {
  import.meta.hot.on('theme-update', async (data) => {
    if (!state.theme) return;

    console.log(`🎨 [HMR] Theme file changed: ${data.file}`);

    // Reload styles
    unloadStyles();
    const originalVersion = state.theme.version;
    state.theme.version = `${originalVersion.split('-')[0]}-${Date.now()}`;
    await loadStyles();
    state.theme.version = originalVersion;

    console.log(`✅ [HMR] Theme "${state.theme.slug}" reloaded!`);
  });
}

// ============================================================
// EXPORT
// ============================================================

export const Asset = {
  // Initialization
  init,
  loadTheme,
  setTheme,
  loadStyles,
  unloadStyles,

  // Theme Stack v2.0
  loadStack,
  loadStackStyles,
  getStack,
  getLoadedTheme,

  // Theme info
  getTheme,
  getVersion,
  getSource,
  isLoaded,

  // URL building (internal, but exposed for edge cases)
  buildUrl,
  loadMapping,

  // Asset getters (main API)
  img,
  sound,
  music,
  icon,
  video,
  get,
  has,

  // Preloading
  preload,
  queue, // alias for compatibility
  subscribe,
  getState,
  isPreloading,
  waitForComplete,

  // Cleanup
  clear,
  clearMemory,
};

export default Asset;
